import useUserStore from "@/state/user/useUserStore";
import React from "react";
import { AnimatePresence, m } from "motion/react";

import {
  ArrowDown,
  ArrowUp,
  FlipHorizontal2,
  FlipVertical2,
  Type,
  X,
} from "lucide-react";
import Image from "@/components/ui/image";
import NextImage from "next/image";
import { MobileHoverBtn } from "@/components/styles";
import { ScrollView } from "react95";

const LayersComponent = ({
  parentVariants,
  color,
}: {
  parentVariants: any;
  color: string;
}) => {
  const [
    assets,
    updateAsset,
    setAssets,
    textFields,
    updateTextField,
    removeTextField,
    uploadAssets,
    setUploadAsset,
    setUploadAssets,
    updateUploadAsset,
  ] = useUserStore((state) => [
    state.assets,
    state.updateAsset,
    state.setAssets,
    state.textFields,
    state.updateTextField,
    state.removeTextField,
    state.uploadAssets,
    state.setUploadAsset,
    state.setUploadAssets,
    state.updateUploadAsset,
  ]);

  const allFields = [...textFields, ...assets, ...uploadAssets].sort(
    (a: any, b: any) => b.zIndex - a.zIndex
  );

  const getType = (field: any) => {
    if (field.hasOwnProperty("ratio")) {
      return "uploadAsset";
    }
    if (field.hasOwnProperty("text")) {
      return "textField";
    }
    return "asset";
  };

  const bringToFront = (index: number, asset: any) => {
    if (index - 1 >= 0) {
      const prevAsset = allFields[index - 1];
      const currentZIndex = asset.zIndex;
      const prevZIndex = prevAsset.zIndex;
      const prevAssetType = getType(prevAsset);
      const assetType = getType(asset);

      switch (prevAssetType) {
        case "uploadAsset":
          setUploadAsset(prevAsset.id, { zIndex: currentZIndex });
          break;
        case "textField":
          updateTextField(prevAsset.id, { zIndex: currentZIndex });
          break;
        case "asset":
          updateAsset(prevAsset.id, { zIndex: currentZIndex });
          break;
      }

      switch (assetType) {
        case "uploadAsset":
          setUploadAsset(asset.id, { zIndex: prevZIndex });
          break;
        case "textField":
          updateTextField(asset.id, { zIndex: prevZIndex });
          break;
        case "asset":
          updateAsset(asset.id, { zIndex: prevZIndex });
          break;
      }
    }
  };

  const bringToBack = (index: number, asset: any) => {
    if (index + 1 < allFields.length) {
      const nextAsset = allFields[index + 1];
      const currentZIndex = asset.zIndex;
      const nextZIndex = nextAsset.zIndex;
      const nextAssetType = getType(nextAsset);
      const assetType = getType(asset);

      switch (nextAssetType) {
        case "uploadAsset":
          updateUploadAsset(nextAsset.id, { zIndex: currentZIndex });
          break;
        case "textField":
          updateTextField(nextAsset.id, { zIndex: currentZIndex });
          break;
        case "asset":
          updateAsset(nextAsset.id, { zIndex: currentZIndex });
          break;
      }

      switch (assetType) {
        case "uploadAsset":
          updateUploadAsset(asset.id, { zIndex: nextZIndex });
          break;
        case "textField":
          updateTextField(asset.id, { zIndex: nextZIndex });
          break;
        case "asset":
          updateAsset(asset.id, { zIndex: nextZIndex });
          break;
      }
    }
  };

  const remove = (asset: any) => {
    const assetType = getType(asset);
    switch (assetType) {
      case "uploadAsset":
        setUploadAssets(uploadAssets.filter((a) => a.id !== asset.id));
        break;
      case "textField":
        removeTextField(asset.id);
        break;
      case "asset":
        setAssets(assets.filter((a) => a.id !== asset.id));
        break;
    }

    allFields.forEach((a) => {
      if (a.zIndex > asset.zIndex) {
        const updatedZIndex = a.zIndex - 1;
        switch (getType(a)) {
          case "uploadAsset":
            updateUploadAsset(a.id, { zIndex: updatedZIndex });
            break;
          case "textField":
            updateTextField(a.id, { zIndex: updatedZIndex });
            break;
          case "asset":
            updateAsset(a.id, { zIndex: updatedZIndex });
            break;
        }
      }
    });
  };

  return (
    <m.div
      variants={parentVariants}
      initial="initial"
      animate="animate"
      style={{
        background: "#cccccc",
        boxSizing: "border-box",
        borderRadius: "0.25rem",
        overflow: "hidden",
        border: "4px solid #000000",
        boxShadow: "2px 2px 0px 0px #000000",
        // padding: "8px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <m.div
        style={{
          background: color,
          boxSizing: "border-box",
          padding: "8px",
          borderBottom: "4px solid #000000",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            padding: "0 8px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "var(--font-cabin-condensed)",
              fontSize: "20px",
              height: "3rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              color: "#000",
            }}
          >
            Layers
          </p>
        </div>
      </m.div>
      <ScrollView
        style={{
          height: `calc(100% - 68px)`,
          margin: "0",
          padding: "0",
          overflow: "hiiden",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            padding: "8px",
            marginBottom: "8px",
            boxSizing: "border-box",
          }}
        >
          <AnimatePresence>
            {allFields.map((asset: any, index: number) => (
              <m.div
                key={asset.id}
                exit={{ opacity: 0, transition: { duration: 0.15 } }}
                transition={{ duration: 0.2 }}
                layout
                style={{
                  width: "100%",
                  height: "96px",
                  aspectRatio: "1/1",
                  padding: "8px 12px",
                  boxSizing: "border-box",
                  boxShadow: "2px 2px 0px 0px #000000",
                  border: "4px solid #000000",
                  borderRadius: "0.25rem",
                  position: "relative",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#cccccc",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "row",

                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <MobileHoverBtn
                      whileHover={{
                        scale: 1.03,
                        transition: {
                          duration: 0.15,
                          ease: "linear",
                        },
                      }}
                      whileTap={{ scale: 0.98 }}
                      color={"#ffffff"}
                      onClick={() => bringToFront(index, asset)}
                      disabled={index === 0}
                    >
                      <ArrowUp style={{ width: "16px", height: "16px" }} />
                    </MobileHoverBtn>
                    <MobileHoverBtn
                      whileHover={{
                        scale: 1.03,
                        transition: {
                          duration: 0.15,
                          ease: "linear",
                        },
                      }}
                      whileTap={{ scale: 0.98 }}
                      color={"#ffffff"}
                      onClick={() => bringToBack(index, asset)}
                      disabled={index + 1 === allFields.length}
                    >
                      <ArrowDown style={{ width: "16px", height: "16px" }} />
                    </MobileHoverBtn>
                  </div>
                  <div
                    style={{
                      height: "100%",
                      aspectRatio: "1/1",
                      position: "relative",
                      padding: "8px",
                      boxSizing: "border-box",
                      marginRight: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "4px",
                    }}
                  >
                    {(() => {
                      switch (getType(asset)) {
                        case "asset":
                          return (
                            <Image
                              src={asset.img}
                              alt={asset.id}
                              fill
                              width={100}
                              style={{
                                objectFit: "contain",
                              }}
                            />
                          );
                        case "uploadAsset":
                          return (
                            <div
                              style={{
                                width: "100%",
                                height: "100%",
                                position: "relative",
                              }}
                            >
                              <NextImage
                                src={asset.img}
                                alt={asset.id}
                                fill
                                style={{
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          );
                        case "textField":
                          return (
                            <Type
                              style={{
                                width: "42px",
                                height: "42px",
                                boxSizing: "border-box",
                              }}
                            />
                          );
                        default:
                          return null;
                      }
                    })()}
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      color: "#000",
                      maxWidth: "96px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getType(asset) === "asset"
                      ? "Asset"
                      : getType(asset) === "uploadAsset"
                      ? "Upload"
                      : "Text Field"}{" "}
                    {asset.index}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {getType(asset) !== "textField" && (
                    <>
                      <MobileHoverBtn
                        whileHover={{
                          scale: 1.03,
                          transition: {
                            duration: 0.15,
                            ease: "linear",
                          },
                        }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (getType(asset) === "uploadAsset") {
                            updateUploadAsset(asset.id, {
                              flipVertical: !asset.flipVertical,
                            });
                          } else {
                            updateAsset(asset.id, {
                              flipVertical: !asset.flipVertical,
                            });
                          }
                        }}
                      >
                        <FlipVertical2
                          style={{ width: "16px", height: "16px" }}
                        />
                      </MobileHoverBtn>
                      <MobileHoverBtn
                        whileHover={{
                          scale: 1.03,
                          transition: {
                            duration: 0.15,
                            ease: "linear",
                          },
                        }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (getType(asset) === "uploadAsset") {
                            updateUploadAsset(asset.id, {
                              flipHorizontal: !asset.flipHorizontal,
                            });
                          } else {
                            updateAsset(asset.id, {
                              flipHorizontal: !asset.flipHorizontal,
                            });
                          }
                        }}
                      >
                        <FlipHorizontal2
                          style={{ width: "16px", height: "16px" }}
                        />
                      </MobileHoverBtn>
                    </>
                  )}
                  <MobileHoverBtn
                    whileHover={{
                      scale: 1.03,
                      transition: {
                        duration: 0.15,
                        ease: "linear",
                      },
                    }}
                    whileTap={{ scale: 0.98 }}
                    color={color}
                    onClick={() => remove(asset)}
                  >
                    <X style={{ width: "16px", height: "16px" }} />
                  </MobileHoverBtn>
                </div>
              </m.div>
            ))}
          </AnimatePresence>
        </div>
      </ScrollView>
    </m.div>
  );
};

export default LayersComponent;
